/**
 * SOLIS FORTUNE — motion engine
 *
 * Design rules encoded here:
 *  - Only opacity and transform are animated. Nothing that triggers layout.
 *  - Every ambient loop is driven by one shared rAF, paused when off-screen
 *    or when the tab is hidden. Idle CPU should read zero.
 *  - Reduced-motion is honoured per-effect, not by a blanket kill switch.
 *  - Everything is idempotent so the theme editor can re-render freely.
 */

const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
const COARSE = matchMedia('(pointer: coarse)');

/* ------------------------------------------------------------------ *
 * Shared rAF ticker — one loop for every continuous effect.
 * ------------------------------------------------------------------ */
const ticker = {
  jobs: new Set(),
  running: false,
  add(fn) {
    this.jobs.add(fn);
    this.start();
  },
  remove(fn) {
    this.jobs.delete(fn);
    if (!this.jobs.size) this.running = false;
  },
  start() {
    if (this.running || !this.jobs.size || document.hidden) return;
    this.running = true;
    const step = (t) => {
      if (!this.running) return;
      for (const fn of this.jobs) fn(t);
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  },
};

document.addEventListener('visibilitychange', () => {
  if (document.hidden) ticker.running = false;
  else ticker.start();
});

/* ------------------------------------------------------------------ *
 * 1. Reveal on scroll
 * ------------------------------------------------------------------ */
const revealObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      entry.target.classList.add('is-in');
      revealObserver.unobserve(entry.target);
    }
  },
  { rootMargin: '0px 0px -12% 0px', threshold: 0.12 }
);

/**
 * Stagger children of a [data-sf-stagger] container by writing a per-item
 * custom property rather than an inline transition — keeps the timing
 * curve in CSS where a designer can change it.
 */
function initReveal(root) {
  for (const group of root.querySelectorAll('[data-sf-stagger]')) {
    const step = Number(group.dataset.sfStagger) || 90;
    const items = group.querySelectorAll(':scope > [data-sf-reveal]');
    items.forEach((el, i) => {
      el.style.setProperty('--sf-reveal-delay', `${i * step}ms`);
    });
  }

  for (const el of root.querySelectorAll('[data-sf-reveal]')) {
    if (el.classList.contains('is-in')) continue;
    revealObserver.observe(el);
  }
}

/* ------------------------------------------------------------------ *
 * 2. Word-split display headings
 * ------------------------------------------------------------------ */
function initSplit(root) {
  for (const el of root.querySelectorAll('[data-sf-split]:not([data-sf-split-done])')) {
    el.dataset.sfSplitDone = '';

    // Preserve the accessible reading of the heading; the visual words are
    // decorative wrappers around the same text.
    const source = el.textContent.trim();
    el.setAttribute('aria-label', source);

    const step = Number(el.dataset.sfSplit) || 55;
    const words = source.split(/\s+/);
    el.textContent = '';

    words.forEach((word, i) => {
      const outer = document.createElement('span');
      outer.className = 'sf-split__word';
      outer.setAttribute('aria-hidden', 'true');

      const inner = document.createElement('span');
      inner.className = 'sf-split__inner';
      inner.style.setProperty('--sf-word-delay', `${i * step}ms`);
      inner.textContent = word;

      outer.appendChild(inner);
      el.appendChild(outer);
      if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
    });

    if (!el.hasAttribute('data-sf-reveal')) el.setAttribute('data-sf-reveal', 'fade');
  }
}

/* ------------------------------------------------------------------ *
 * 3. Parallax — transform-only, driven by the shared ticker
 * ------------------------------------------------------------------ */
function initParallax(root) {
  if (REDUCED.matches || COARSE.matches) return;

  const layers = [...root.querySelectorAll('[data-sf-parallax]:not([data-sf-parallax-done])')];
  if (!layers.length) return;

  for (const el of layers) el.dataset.sfParallaxDone = '';

  const visible = new Set();
  const vis = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) visible.add(e.target);
        else visible.delete(e.target);
      }
      if (visible.size) ticker.add(tick);
      else ticker.remove(tick);
    },
    { rootMargin: '20% 0px' }
  );

  function tick() {
    const vh = innerHeight;
    for (const el of visible) {
      const depth = Number(el.dataset.sfParallax) || 0.12;
      const rect = el.getBoundingClientRect();
      // -1 at the bottom of the viewport, +1 at the top: symmetric travel.
      const progress = (rect.top + rect.height / 2 - vh / 2) / vh;
      el.style.transform = `translate3d(0, ${(-progress * depth * 100).toFixed(2)}px, 0)`;
    }
  }

  for (const el of layers) vis.observe(el);
}

/* ------------------------------------------------------------------ *
 * 4. Magnetic buttons — pointer-following, spring-free
 * ------------------------------------------------------------------ */
function initMagnetic(root) {
  if (REDUCED.matches || COARSE.matches) return;

  for (const el of root.querySelectorAll('[data-sf-magnetic]:not([data-sf-magnetic-done])')) {
    el.dataset.sfMagneticDone = '';
    const pull = Number(el.dataset.sfMagnetic) || 0.28;

    el.addEventListener('pointermove', (e) => {
      const r = el.getBoundingClientRect();
      const dx = (e.clientX - (r.left + r.width / 2)) * pull;
      const dy = (e.clientY - (r.top + r.height / 2)) * pull;
      el.style.setProperty('--_shift', `${dx.toFixed(1)}px`);
      el.style.transform = `translate3d(${dx.toFixed(1)}px, ${dy.toFixed(1)}px, 0)`;
    });

    el.addEventListener('pointerleave', () => {
      el.style.removeProperty('--_shift');
      el.style.transform = '';
    });
  }
}

/* ------------------------------------------------------------------ *
 * 5. Count-up numbers
 * ------------------------------------------------------------------ */
function initCounters(root) {
  const els = [...root.querySelectorAll('[data-sf-count]:not([data-sf-count-done])')];
  if (!els.length) return;

  const obs = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting) continue;
        const el = e.target;
        obs.unobserve(el);
        el.dataset.sfCountDone = '';

        const target = parseFloat(el.dataset.sfCount);
        if (Number.isNaN(target)) continue;

        const decimals = Number(el.dataset.sfCountDecimals) || 0;
        const prefix = el.dataset.sfCountPrefix || '';
        const suffix = el.dataset.sfCountSuffix || '';
        const format = (v) =>
          prefix + v.toLocaleString(undefined, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
          }) + suffix;

        if (REDUCED.matches) {
          el.textContent = format(target);
          continue;
        }

        const dur = Number(el.dataset.sfCountDuration) || 1600;
        const t0 = performance.now();

        const run = (now) => {
          const p = Math.min((now - t0) / dur, 1);
          // easeOutExpo — fast commit, long settle. Reads as confident.
          const eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
          el.textContent = format(target * eased);
          if (p < 1) requestAnimationFrame(run);
        };
        requestAnimationFrame(run);
      }
    },
    { threshold: 0.5 }
  );

  for (const el of els) obs.observe(el);
}

/* ------------------------------------------------------------------ *
 * 6. Header state — condensed after the fold
 * ------------------------------------------------------------------ */
function initHeaderState() {
  if (document.documentElement.dataset.sfHeaderDone !== undefined) return;
  document.documentElement.dataset.sfHeaderDone = '';

  // Tinker locks html/body to 100dvh above 990px and scrolls .page-wrapper
  // instead. A sentinel parked on <body> therefore never moves on desktop —
  // it has to live inside whichever element actually scrolls.
  const scroller = document.querySelector('.page-wrapper') || document.body;

  const sentinel = document.createElement('div');
  sentinel.setAttribute('aria-hidden', 'true');
  sentinel.style.cssText = 'position:absolute;top:0;left:0;width:1px;height:80px;pointer-events:none;';
  if (getComputedStyle(scroller).position === 'static') scroller.style.position = 'relative';
  scroller.prepend(sentinel);

  new IntersectionObserver(
    ([e]) => document.documentElement.classList.toggle('sf-scrolled', !e.isIntersecting),
    { threshold: 0 }
  ).observe(sentinel);
}

/* ------------------------------------------------------------------ *
 * Boot + theme-editor re-entry
 * ------------------------------------------------------------------ */
/* ------------------------------------------------------------------ *
 * 7. Card ratings — move the server-rendered rating under the title
 * ------------------------------------------------------------------ */
function initRatings(root) {
  for (const rating of root.querySelectorAll('[data-solis-rating]:not([data-rating-placed])')) {
    rating.dataset.ratingPlaced = '';
    const card = rating.closest('.product-card__content') || rating.parentElement;
    if (!card) continue;

    // The title is whichever heading or product-title block comes first; the
    // rating belongs immediately after it, above the price. Tinker also
    // renders a hidden duplicate title inside its zoom-out details wrapper —
    // parking the rating there makes it invisible, so those are skipped.
    const candidates = card.querySelectorAll('h1, h2, h3, h4, [class*="product-title"], [class*="card__title"]');
    let title = null;
    for (const el of candidates) {
      if (!el.closest('[class*="zoom-out"]')) {
        title = el;
        break;
      }
    }
    if (title && title.parentElement) {
      title.parentElement.insertBefore(rating, title.nextSibling);
    }
  }
}

/* ------------------------------------------------------------------ *
 * 8. Mark nav items that have a submenu so the caret can be styled
 * ------------------------------------------------------------------ */
function initNavCarets(root) {
  const items = root.querySelectorAll('li:not([data-caret-checked])');
  for (const li of items) {
    li.dataset.caretChecked = '';
    const hasChild = li.querySelector('ul, [class*="submenu"], [class*="mega-menu"]');
    if (hasChild) li.classList.add('solis-has-children');
  }
}

/* ------------------------------------------------------------------ *
 * 9. Countdown — one clock for the whole store.
 *
 * Markup comes from snippets/solis-cd.liquid; timing comes from theme
 * settings, so every instance (announcement bar, urgency band, collection
 * banner, product buy box) shows the same clock and is changed in ONE
 * place. Recurring cycles are computed from the anchor, not the visitor's
 * first view — no personalised fake deadlines.
 * ------------------------------------------------------------------ */
class SolisCountdown extends HTMLElement {
  connectedCallback() {
    this.recurring = this.dataset.mode === 'recurring';
    this.clock = this.querySelector('[data-ref="clock"]');
    this.expired = this.querySelector('[data-ref="expired"]');
    this.units = {};
    for (const el of this.querySelectorAll('[data-unit]')) this.units[el.dataset.unit] = el;

    if (this.recurring) {
      this.anchor = new Date(this.dataset.anchor).getTime();
      this.cycleMs = Number(this.dataset.cycleHours) * 3600 * 1000;
      this.valid = Number.isFinite(this.anchor) && this.cycleMs > 0;
    } else {
      this.target = new Date(this.dataset.deadline).getTime();
      this.valid = Number.isFinite(this.target);
    }

    // An unparseable or missing date is an authoring mistake, not a state
    // to render. Stay hidden rather than show a broken clock.
    if (!this.valid) {
      this.hidden = true;
      return;
    }

    this.render();
    this.timer = setInterval(() => this.render(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  /** Milliseconds until the current cycle closes. Always > 0. */
  remainingRecurring(now) {
    const elapsed = now - this.anchor;
    // Modulo handles both directions: a future anchor counts down to its
    // own start, a past anchor rolls forward through completed cycles.
    const intoCycle = ((elapsed % this.cycleMs) + this.cycleMs) % this.cycleMs;
    return this.cycleMs - intoCycle;
  }

  render() {
    const now = Date.now();
    const left = this.recurring ? this.remainingRecurring(now) : this.target - now;

    if (left <= 0) {
      clearInterval(this.timer);
      this.clock.hidden = true;
      if (this.dataset.expiredMode === 'hide') this.hidden = true;
      else this.expired.hidden = false;
      return;
    }

    if (this.clock.hidden) this.clock.hidden = false;
    const s = Math.floor(left / 1000);
    const pad = (n) => String(n).padStart(2, '0');
    // perf-1017: textContent= swaps the text node every second, and node insertion invalidates
    // the theme's :has() rules up to <body> -> a full-page style recalc per tick per clock.
    // Editing the existing text node (characterData) does not, and unchanged units are skipped.
    const set = (el, v) => {
      const t = el.firstChild;
      if (t && t.nodeType === 3 && !t.nextSibling) {
        if (t.data !== v) t.data = v;
      } else if (el.textContent !== v) {
        el.textContent = v;
      }
    };
    set(this.units.days, pad(Math.floor(s / 86400)));
    set(this.units.hours, pad(Math.floor((s % 86400) / 3600)));
    set(this.units.minutes, pad(Math.floor((s % 3600) / 60)));
    set(this.units.seconds, pad(s % 60));
  }
}

if (!customElements.get('solis-countdown')) {
  customElements.define('solis-countdown', SolisCountdown);
}

function boot(root = document) {
  // Split first: it stamps data-sf-reveal onto headings that initReveal
  // then needs to observe. Reversing this order silently drops the animation.
  initSplit(root);
  initReveal(root);
  initParallax(root);
  initMagnetic(root);
  initCounters(root);
  initRatings(root);
  initNavCarets(root);
}

// Safety net only — the head script in layout/theme.liquid already set this
// before first paint so the hidden state never flashes.
document.documentElement.classList.add('sf-motion');

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    boot();
    initHeaderState();
  });
} else {
  boot();
  initHeaderState();
}

// Shopify re-renders a section's DOM on every editor change; re-scan just it.
document.addEventListener('shopify:section:load', (e) => boot(e.target));
document.addEventListener('shopify:block:select', (e) => boot(e.target));
